import type { Feature, ExtrudeFeature, LoftFeature, RevolveFeature, SweepFeature, MirrorFeature, LinearPatternFeature, CircularPatternFeature, FilletFeature, ChamferFeature, ShellFeature, DraftFeature, SketchFeature, SketchCommand, ImportedPartFeature, AssemblyLinearPatternFeature, AssemblyCircularPatternFeature, SheetMetalBaseFeature, SheetMetalFlangeFeature, SheetMetalCornerReliefFeature, ProfileType, Vec3 } from "./featureTypes";
import { isBooleanFeature, isModifierFeature } from "./featureTypes";

const AXIS_VEC: Record<string, string> = { X: "[1, 0, 0]", Y: "[0, 1, 0]", Z: "[0, 0, 1]" };

/** Convert sketch commands to a replicad Sketcher method chain */
function sketchCommandsToCode(commands: SketchCommand[], plane: string, closed: boolean): string {
  const parts: string[] = [`new Sketcher("${plane}")`];
  for (const cmd of commands) {
    switch (cmd.tool) {
      case "moveTo":
        parts.push(`  .movePointerTo([${cmd.point[0]}, ${cmd.point[1]}])`);
        break;
      case "lineTo":
        parts.push(`  .lineTo([${cmd.point[0]}, ${cmd.point[1]}])`);
        break;
      case "arcTo":
        parts.push(`  .sagittaArcTo([${cmd.point[0]}, ${cmd.point[1]}], ${cmd.sagitta})`);
        break;
      case "bezierTo":
        parts.push(`  .cubicBezierCurveTo([${cmd.point[0]}, ${cmd.point[1]}], [${cmd.cp1[0]}, ${cmd.cp1[1]}], [${cmd.cp2[0]}, ${cmd.cp2[1]}])`);
        break;
      case "splineTo":
        parts.push(`  .smoothSplineTo([${cmd.point[0]}, ${cmd.point[1]}])`);
        break;
    }
  }
  parts.push(closed ? "  .close()" : "  .done()");
  return parts.join("\n    ");
}

/** Build a sketch expression for a profile type and add the required import. */
function profileSketch(
  profile: ProfileType,
  width: number, height: number, radius: number,
  imports: Set<string>,
  suffix = "",
): string {
  if (profile === "circle") {
    imports.add("sketchCircle");
    return `sketchCircle(${radius}${suffix})`;
  }
  if (profile === "rounded-rectangle") {
    imports.add("sketchRoundedRectangle");
    return `sketchRoundedRectangle(${width}, ${height}, ${radius}${suffix})`;
  }
  imports.add("sketchRectangle");
  return `sketchRectangle(${width}, ${height}${suffix})`;
}

interface GenerateOpts {
  /** When true, emits __featureShapes() returning per-feature shape map */
  featureMap?: boolean;
  /** Explosion scale factor (0 = no explosion, 1 = full explosion offset) */
  explodeScale?: number;
}

function primitiveArgs(f: Feature): { fn: string; args: string } | null {
  switch (f.type) {
    case "box": return { fn: "makeBaseBox", args: `${f.width}, ${f.height}, ${f.depth}` };
    case "cylinder": return { fn: "makeCylinder", args: `${f.radius}, ${f.height}` };
    case "sphere": return { fn: "makeSphere", args: `${f.radius}` };
    default: return null;
  }
}

/**
 * Compile a feature array into a replicad script string.
 * Pure function — no side effects.
 */
export function generateScript(features: Feature[], opts?: GenerateOpts): string {
  const featureMap = opts?.featureMap ?? false;
  const enabled = features.filter((f) => f.enabled);
  if (enabled.length === 0) {
    return "function main() {\n  return null;\n}\n";
  }

  const imports = new Set<string>();
  const lines: string[] = [];
  const varMap = new Map<string, string>();
  const consumed = new Set<string>();
  const extraVars: string[] = [];
  const emittedImports = new Set<string>();
  const decl = featureMap ? "" : "const ";

  enabled.forEach((f, i) => {
    const v = `s${i}`;
    const primArgs = primitiveArgs(f);

    if (primArgs) {
      varMap.set(f.id, v);
      imports.add(primArgs.fn);
      const pos = (f as { position: Vec3 }).position;
      let line = `  ${decl}${v} = ${primArgs.fn}(${primArgs.args})`;
      if (pos[0] !== 0 || pos[1] !== 0 || pos[2] !== 0) {
        line += `.translate(${pos[0]}, ${pos[1]}, ${pos[2]})`;
      }
      lines.push(line + ";");
    } else {
      processFeature(f, v, decl, varMap, imports, lines, consumed, extraVars, emittedImports, opts, enabled);
    }
  });

  return assembleScript(varMap, extraVars, imports, lines, consumed, enabled, featureMap);
}

function processFeature(
  f: Feature,
  v: string,
  decl: string,
  varMap: Map<string, string>,
  imports: Set<string>,
  lines: string[],
  consumed: Set<string>,
  extraVars: string[],
  emittedImports: Set<string>,
  opts?: GenerateOpts,
  enabled?: Feature[]
) {
  const featureMap = opts?.featureMap ?? false;
  /** Helper to use a shape var: clones if in featureMap mode to prevent consumption by CSG ops. */
  const use = (varName: string) => featureMap ? `${varName}.clone()` : varName;

  if (isBooleanFeature(f)) {
    const target = varMap.get(f.targetRef);
    const tool = varMap.get(f.toolRef);
    if (target && tool) {
      varMap.set(f.id, v);
      lines.push(`  ${decl}${v} = ${use(target)}.${f.type}(${use(tool)});`);
      consumed.add(f.targetRef);
      consumed.add(f.toolRef);
    } else {
      lines.push(`  // ${f.type}: missing target or tool ref`);
    }
  } else if (isModifierFeature(f)) {
    const target = varMap.get(f.targetRef);
    if (target) {
      varMap.set(f.id, v);
      if (f.type === "shell") {
        const t = (f as ShellFeature).thickness;
        lines.push(`  ${decl}${v} = ${use(target)}.shell(${t}, (f) => f.inPlane("XY"));`);
      } else if (f.type === "draft") {
        imports.add("draft");
        const df = f as DraftFeature;
        lines.push(`  ${decl}${v} = draft(${use(target)}, ${df.angle}, ${AXIS_VEC[df.axis]});`);
      } else {
        const arg = f.type === "fillet" ? (f as FilletFeature).radius : (f as ChamferFeature).distance;
        lines.push(`  ${decl}${v} = ${use(target)}.${f.type}(${arg});`);
      }
      consumed.add(f.targetRef);
    } else {
      lines.push(`  // ${f.type}: missing target ref`);
    }
  } else if (f.type === "extrude") {
    varMap.set(f.id, v);
    const ef = f as ExtrudeFeature;
    const pos = ef.position;
    const sketchExpr = profileSketch(ef.profile, ef.profileWidth ?? 20, ef.profileHeight ?? 20, ef.profileRadius ?? (ef.profile === "circle" ? 10 : 3), imports);
    const eOpts: string[] = [];
    if (ef.twistAngle && ef.twistAngle !== 0) eOpts.push(`twistAngle: ${ef.twistAngle}`);
    if (ef.taperFactor !== undefined && ef.taperFactor !== 1) eOpts.push(`extrusionProfile: { profile: "linear", endFactor: ${ef.taperFactor} }`);
    const extrudeArgs = eOpts.length > 0 ? `${ef.depth}, { ${eOpts.join(", ")} }` : `${ef.depth}`;
    let line = `  ${decl}${v} = ${sketchExpr}.extrude(${extrudeArgs})`;
    if (pos[0] !== 0 || pos[1] !== 0 || pos[2] !== 0) line += `.translate(${pos[0]}, ${pos[1]}, ${pos[2]})`;
    lines.push(line + ";");
  } else if (f.type === "loft") {
    varMap.set(f.id, v);
    const lf = f as LoftFeature;
    const startExpr = profileSketch(lf.startProfile, lf.startWidth, lf.startHeight, lf.startRadius, imports);
    const endExpr = profileSketch(lf.endProfile, lf.endWidth, lf.endHeight, lf.endRadius, imports, `, { origin: [0, 0, ${lf.height}] }`);
    if (featureMap) extraVars.push(`${v}_start`, `${v}_end`);
    lines.push(`  ${decl}${v}_start = ${startExpr};`);
    lines.push(`  ${decl}${v}_end = ${endExpr};`);
    const loftOpts = lf.ruled ? ", { ruled: true }" : "";
    let line = `  ${decl}${v} = ${v}_start.loftWith(${v}_end${loftOpts})`;
    const pos = lf.position;
    if (pos[0] !== 0 || pos[1] !== 0 || pos[2] !== 0) line += `.translate(${pos[0]}, ${pos[1]}, ${pos[2]})`;
    lines.push(line + ";");
  } else if (f.type === "revolve") {
    varMap.set(f.id, v);
    const rf = f as RevolveFeature;
    const offsetSuffix = rf.offset !== 0 ? `, { origin: [${rf.offset}, 0, 0] }` : "";
    const sketchExpr = profileSketch(rf.profile, rf.profileWidth ?? 10, rf.profileHeight ?? 5, rf.profileRadius ?? (rf.profile === "circle" ? 3 : 1), imports, offsetSuffix);
    const angle = rf.angle ?? 360;
    let line: string;
    if (angle !== 360) {
      imports.add("revolution");
      if (featureMap) extraVars.push(`${v}_face`);
      lines.push(`  ${decl}${v}_face = ${sketchExpr}.face();`);
      line = `  ${decl}${v} = revolution(${v}_face, [0, 0, 0], ${AXIS_VEC[rf.axis]}, ${angle})`;
    } else {
      line = `  ${decl}${v} = ${sketchExpr}.revolve(${AXIS_VEC[rf.axis]})`;
    }
    const pos = rf.position;
    if (pos[0] !== 0 || pos[1] !== 0 || pos[2] !== 0) line += `.translate(${pos[0]}, ${pos[1]}, ${pos[2]})`;
    lines.push(line + ";");
  } else if (f.type === "sweep") {
    varMap.set(f.id, v);
    const sf = f as SweepFeature;
    const height = sf.pitch * sf.turns;
    imports.add("sketchHelix");
    imports.add("sketchCircle");
    if (featureMap) extraVars.push(`${v}_path`);
    lines.push(`  ${decl}${v}_path = sketchHelix(${sf.pitch}, ${height}, ${sf.helixRadius});`);
    let line = `  ${decl}${v} = ${v}_path.sweepSketch((plane, origin) => sketchCircle(${sf.profileRadius}, { plane, origin }))`;
    const pos = sf.position;
    if (pos[0] !== 0 || pos[1] !== 0 || pos[2] !== 0) line += `.translate(${pos[0]}, ${pos[1]}, ${pos[2]})`;
    lines.push(line + ";");
  } else if (f.type === "mirror") {
    const mf = f as MirrorFeature;
    const target = varMap.get(mf.targetRef);
    if (target) {
      varMap.set(f.id, v);
      lines.push(`  ${decl}${v} = ${use(target)}.fuse(${use(target)}.mirror("${mf.plane}"));`);
      consumed.add(mf.targetRef);
    } else {
      lines.push(`  // mirror: missing target ref`);
    }
  } else if (f.type === "linearPattern" || f.type === "circularPattern") {
    const p = f as (LinearPatternFeature | CircularPatternFeature);
    const target = varMap.get(p.targetRef);
    if (target) {
      varMap.set(f.id, v);
      if (featureMap) extraVars.push(`${v}_copies`);
      lines.push(`  ${decl}${v}_copies = [${use(target)}];`);

      let patternTransform = "";
      if (p.type === "linearPattern") {
        const lp = p as LinearPatternFeature;
        const axisTrans = { X: `j * ${lp.spacing}, 0, 0`, Y: `0, j * ${lp.spacing}, 0`, Z: `0, 0, j * ${lp.spacing}` }[lp.axis];
        patternTransform = `translate(${axisTrans})`;
      } else {
        const cp = p as CircularPatternFeature;
        patternTransform = `rotate(j * ${cp.angle / cp.count}, [0, 0, 0], ${AXIS_VEC[cp.axis]})`;
      }

      lines.push(`  for (let j = 1; j < ${p.count}; j++) { ${v}_copies.push(${use(target)}.${patternTransform}); }`);
      lines.push(`  ${decl}${v} = ${v}_copies.reduce((a, b) => a.fuse(b));`);
      consumed.add(p.targetRef);
    } else {
      lines.push(`  // ${f.type}: missing target ref`);
    }
  } else if (f.type === "sketch") {
    const sf = f as SketchFeature;
    if (sf.commands.length < 2) {
      lines.push(`  // sketch: not enough commands`);
    } else {
      varMap.set(f.id, v);
      imports.add("Sketcher");
      const sketchExpr = sketchCommandsToCode(sf.commands, sf.plane, sf.closed);
      let line: string;
      if (sf.operation === "revolve") {
        const axis = AXIS_VEC[sf.revolveAxis ?? "Z"];
        const angle = sf.revolveAngle ?? 360;
        if (angle !== 360) {
          imports.add("revolution");
          if (featureMap) extraVars.push(`${v}_face`);
          lines.push(`  ${decl}${v}_face = ${sketchExpr}.face();`);
          line = `  ${decl}${v} = revolution(${v}_face, [0, 0, 0], ${axis}, ${angle})`;
        } else {
          line = `  ${decl}${v} = ${sketchExpr}\n    .revolve(${axis})`;
        }
      } else {
        const sOpts: string[] = [];
        if (sf.twistAngle && sf.twistAngle !== 0) sOpts.push(`twistAngle: ${sf.twistAngle}`);
        if (sf.taperFactor !== undefined && sf.taperFactor !== 1) sOpts.push(`extrusionProfile: { profile: "linear", endFactor: ${sf.taperFactor} }`);
        const extrudeArgs = sOpts.length > 0 ? `${sf.depth}, { ${sOpts.join(", ")} }` : `${sf.depth}`;
        line = `  ${decl}${v} = ${sketchExpr}\n    .extrude(${extrudeArgs})`;
      }
      const pos = sf.position;
      if (pos[0] !== 0 || pos[1] !== 0 || pos[2] !== 0) line += `.translate(${pos[0]}, ${pos[1]}, ${pos[2]})`;
      lines.push(line + ";");
    }
  } else if (f.type === "importedPart") {
    const ip = f as ImportedPartFeature;
    if (ip.importScript) {
      const funcName = `__import_${ip.importDocId.replace(/[^a-zA-Z0-9]/g, "_")}`;

      const importRegex = /const\s*\{([^}]+)\}\s*=\s*replicad;/g;
      let m;
      while ((m = importRegex.exec(ip.importScript)) !== null) {
        const names = m[1].split(",").map((s) => s.trim()).filter(Boolean);
        names.forEach(n => imports.add(n));
      }

      if (!emittedImports.has(funcName)) {
        const scriptBody = ip.importScript.replace(/const\s*\{[^}]+\}\s*=\s*replicad;/, "");
        extraVars.push(`__FUNC_${funcName}`);
        lines.push(`  // Imported: ${ip.importDocName}`);
        lines.push(`  __FUNC_${funcName} = (() => {`);
        lines.push(scriptBody);
        lines.push(`    return main;`);
        lines.push(`  })();`);
        emittedImports.add(funcName);
      }

      varMap.set(f.id, v);
      const es = opts?.explodeScale ?? 0;
      const explode = ip.explosionOffset ?? [0, 0, 0];
      const tx = ip.importPosition[0] + explode[0] * es;
      const ty = ip.importPosition[1] + explode[1] * es;
      const tz = ip.importPosition[2] + explode[2] * es;

      let line = `  ${decl}${v} = __FUNC_${funcName}()`;
      if (ip.importRotation[0] !== 0) line += `.rotate(${ip.importRotation[0]}, [0, 0, 0], [1, 0, 0])`;
      if (ip.importRotation[1] !== 0) line += `.rotate(${ip.importRotation[1]}, [0, 0, 0], [0, 1, 0])`;
      if (ip.importRotation[2] !== 0) line += `.rotate(${ip.importRotation[2]}, [0, 0, 0], [0, 0, 1])`;
      if (tx !== 0 || ty !== 0 || tz !== 0) line += `.translate(${tx}, ${ty}, ${tz})`;
      lines.push(line + ";");
    } else {
      lines.push(`  // importedPart: script not loaded yet`);
    }
  } else if (f.type === "assemblyLinearPattern" || f.type === "assemblyCircularPattern") {
    const ap = f as (AssemblyLinearPatternFeature | AssemblyCircularPatternFeature);
    const target = varMap.get(ap.targetRef);
    if (target) {
      varMap.set(f.id, v);
      if (featureMap) extraVars.push(`${v}_copies`);
      imports.add("compoundShapes");
      lines.push(`  ${decl}${v}_copies = [${use(target)}];`);
      let trans = "";
      if (ap.type === "assemblyLinearPattern") {
        const lp = ap as AssemblyLinearPatternFeature;
        trans = `translate(${{ X: `j * ${lp.spacing}, 0, 0`, Y: `0, j * ${lp.spacing}, 0`, Z: `0, 0, j * ${lp.spacing}` }[lp.axis]})`;
      } else {
        const cp = ap as AssemblyCircularPatternFeature;
        trans = `rotate(j * ${cp.angle / cp.count}, [0, 0, 0], ${AXIS_VEC[cp.axis]})`;
      }
      lines.push(`  for (let j = 1; j < ${ap.count}; j++) { ${v}_copies.push(${use(target)}.${trans}); }`);
      lines.push(`  ${decl}${v} = compoundShapes(${v}_copies);`);
      consumed.add(ap.targetRef);
    } else {
      lines.push(`  // ${f.type}: missing target ref`);
    }
  } else if (f.type === "sheetMetalBase") {
    const sm = f as SheetMetalBaseFeature;
    varMap.set(f.id, v);
    imports.add("makeBox");
    let line = `  ${decl}${v} = makeBox(${sm.width}, ${sm.length}, ${sm.thickness})`;
    if (sm.position[0] !== 0 || sm.position[1] !== 0 || sm.position[2] !== 0) {
      line += `.translate(${sm.position[0]}, ${sm.position[1]}, ${sm.position[2]})`;
    }
    lines.push(line + ";");
    lines.push(`  // sheetMetal: thickness=${sm.thickness} bendRadius=${sm.bendRadius} kFactor=${sm.kFactor}`);
  } else if (f.type === "sheetMetalFlange") {
    const fl = f as SheetMetalFlangeFeature;
    const target = varMap.get(fl.targetRef);
    if (target) {
      varMap.set(f.id, v);
      imports.add("makeBox");
      const baseFeat = enabled?.find((ef) => ef.id === fl.targetRef) as SheetMetalBaseFeature | undefined;
      const t = baseFeat?.thickness ?? 1;
      const angleRad = (fl.flangeAngle * Math.PI / 180).toFixed(4);
      const edgeOffsets: Record<string, string> = {
        right: `${baseFeat?.width ?? 20}, 0, 0`,
        left: `-${fl.flangeLength}, 0, 0`,
        top: `0, ${baseFeat?.length ?? 20}, 0`,
        bottom: `0, -${fl.flangeLength}, 0`,
      };
      const edgeRotations: Record<string, string> = {
        right: `.rotate(${angleRad}, [${baseFeat?.width ?? 20}, 0, 0], [0, 1, 0])`,
        left: `.rotate(-${angleRad}, [0, 0, 0], [0, 1, 0])`,
        top: `.rotate(${angleRad}, [0, ${baseFeat?.length ?? 20}, 0], [1, 0, 0])`,
        bottom: `.rotate(-${angleRad}, [0, 0, 0], [1, 0, 0])`,
      };
      const flangeW = fl.edge === "left" || fl.edge === "right" ? fl.flangeLength : (baseFeat?.width ?? 20);
      const flangeL = fl.edge === "top" || fl.edge === "bottom" ? fl.flangeLength : (baseFeat?.length ?? 20);
      if (featureMap) extraVars.push(`${v}_flange`);
      lines.push(`  ${decl}${v}_flange = makeBox(${flangeW}, ${flangeL}, ${t}).translate(${edgeOffsets[fl.edge]})${edgeRotations[fl.edge]};`);
      lines.push(`  ${decl}${v} = ${use(target)}.fuse(${v}_flange);`);
      consumed.add(fl.targetRef);
    } else {
      lines.push(`  // sheetMetalFlange: missing target ref`);
    }
  } else if (f.type === "sheetMetalCornerRelief") {
    const cr = f as SheetMetalCornerReliefFeature;
    const target = varMap.get(cr.targetRef);
    if (target) {
      varMap.set(f.id, v);
      const baseFeat = enabled?.find((ef) => ef.type === "sheetMetalBase") as SheetMetalBaseFeature | undefined;
      const t = baseFeat?.thickness ?? 1.5;
      const br = baseFeat?.bendRadius ?? 1.5;
      const reliefR = ((t + br) * cr.reliefScale) / 2;
      if (cr.reliefType === "circular") {
        imports.add("makeCylinder");
        if (featureMap) extraVars.push(`${v}_relief`);
        lines.push(`  ${decl}${v}_relief = makeCylinder(${reliefR.toFixed(2)}, ${(t + 1).toFixed(1)}).translateZ(-0.5);`);
      } else {
        imports.add("makeBox");
        const reliefD = reliefR * 2;
        if (featureMap) extraVars.push(`${v}_relief`);
        lines.push(`  ${decl}${v}_relief = makeBox(${reliefD.toFixed(2)}, ${reliefD.toFixed(2)}, ${(t + 1).toFixed(1)}).translate(-${reliefR.toFixed(2)}, -${reliefR.toFixed(2)}, -0.5);`);
      }
      lines.push(`  ${decl}${v} = ${use(target)}; // corner relief applied`);
      consumed.add(cr.targetRef);
    } else {
      lines.push(`  // sheetMetalCornerRelief: missing target ref`);
    }
  } else {
    lines.push(`  // unsupported feature type: ${f.type}`);
  }
}

function assembleScript(
  varMap: Map<string, string>,
  extraVars: string[],
  imports: Set<string>,
  lines: string[],
  consumed: Set<string>,
  enabled: Feature[],
  featureMap: boolean
): string {
  const topLevelEntries = enabled
    .filter((f) => !consumed.has(f.id))
    .map((f) => ({ id: f.id, varName: varMap.get(f.id) }))
    .filter((e): e is { id: string; varName: string } => !!e.varName);

  const topLevelVars = topLevelEntries.map((e) => e.varName);
  let returnExpr = "null";
  if (topLevelVars.length === 1) returnExpr = featureMap ? `${topLevelVars[0]}.clone()` : topLevelVars[0];
  else if (topLevelVars.length > 1) {
    imports.add("compoundShapes");
    const varExprs = featureMap ? topLevelVars.map((v) => `${v}.clone()`) : topLevelVars;
    returnExpr = `compoundShapes([${varExprs.join(", ")}])`;
  }

  const importList = Array.from(imports).sort().join(", ");
  const output: string[] = [`const { ${importList} } = replicad;`, ""];
  if (featureMap) {
    const hoisted = [...new Set([...varMap.values(), ...extraVars])];
    output.push(`let ${hoisted.join(", ")};`, "");
  }
  output.push("function main() {", ...lines, `  return ${returnExpr};`, "}");
  if (featureMap && topLevelEntries.length > 0) {
    const entries = topLevelEntries.map((e) => `"${e.id}": ${e.varName}`).join(", ");
    output.push("", "function __featureShapes() {", `  return { ${entries} };`, "}");
  }
  return output.join("\n") + "\n";
}
